<template>
  <v-dialog v-model="dialog" max-width="700" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title class="grey--text text-subtitle-1">
        Informazioni Termostato
      </v-card-title>
      <v-card-text>
        <v-row no-gutters>
          <v-col cols="12">
            <pre v-if="!$vuetify.breakpoint.xsOnly">Last Update: {{ last_update }} ({{ time_ago }})</pre>
            <pre v-else>Last Update: {{ last_update }}</pre>
          </v-col>
          <v-col cols="12">
            <pre>Uptime: {{ therm.isConnected ? up_time : "N/A" }}</pre>
          </v-col>
          <v-col cols="12">
            <pre>Sid: {{ therm.sid }}</pre>
          </v-col>
          <v-col cols="12">
            <pre>SSID: {{ therm.SSID }}</pre>
          </v-col>
          <v-col cols="12">
            <pre>Local IP: {{ therm.local_ip }}</pre>
          </v-col>
          <v-col cols="12">
            <pre>External IP: {{ therm.external_ip }}</pre>
          </v-col>
          <v-col cols="12">
            <pre>Latenza: {{ avg_latency }} ms</pre>
          </v-col>
          <v-col cols="12">
            <pre>Request Time: {{ avg_req_time }} ms</pre>
          </v-col>
          <v-col cols="12">
            <pre>RSSI: {{ avg_rssi }} dBm</pre>
          </v-col>
          <v-col cols="12">
            <pre>Error Rate: {{ avg_error_rate }}%</pre>
          </v-col>
        </v-row >
        <v-row no-gutters justify="center" class="mt-4">
          <v-col cols="11">
            <VueApexCharts
              type="line"
              height="320"
              :options="chartOptions"
              :series="chartSeries"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="mt-8">
        <v-spacer></v-spacer>
        <v-btn text color="grey" @click="dialog = false">Chiudi</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "ThermInfoBox",
  components: { VueApexCharts },
  props: {
    therm: { type: Object, required: true },
  },
  data() {
    return {
      dialog: false,
    };
  },
  computed: {
    monitoring() {
      return Array.isArray(this.therm.monitoring) ? this.therm.monitoring : [];
    },
    last_update() {
      return this.therm.last_update
        ? new Date(this.therm.last_update * 1000).toLocaleString()
        : "-";
    },
    up_time() {
      const seconds = this.therm.up_time || 0;
      const d = Math.floor(seconds / 86400);
      const h = Math.floor((seconds % 86400) / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const parts = [];
      if (d) parts.push(`${d}d`);
      if (h) parts.push(`${h}h`);
      if (m) parts.push(`${m}m`);
      return parts.join(" ") || "0m";
    },
    time_ago() {
      return this.therm.last_update
        ? moment.unix(this.therm.last_update).fromNow()
        : "-";
    },
    avg_req_time() {
      if (!this.monitoring.length) return "0.0";
      const sum = this.monitoring
        .map((m) => m.request_time || 0)
        .filter((v) => v != 0);
      const avg = sum.reduce((a, m) => a + (m || 0), 0);
      return (avg / sum.length).toFixed(1);
    },
    avg_latency() {
      if (!this.monitoring.length) return "0.0";
      const sum = this.monitoring
        .map((m) => m.latency || 0)
        .filter((v) => v != 0);
      const avg = sum.reduce((a, m) => a + (m || 0), 0);
      return (avg / sum.length).toFixed(1);
    },
    avg_rssi() {
      if (!this.monitoring.length) return "0.0";
      const sum = this.monitoring.map((m) => m.RSSI || 0).filter((v) => v != 0);
      const avg = sum.reduce((a, m) => a + (m || 0), 0);
      return (avg / sum.length).toFixed(1);
    },
    avg_error_rate() {
      if (!this.monitoring.length) return "0.0";
      const sum = this.monitoring
        .map((m) => (m.error_rate >= 0 ? m.error_rate : 0))
        .filter((v) => v != 0);
      const avg = sum.reduce((a, m) => a + (m || 0), 0);
      return (avg / sum.length).toFixed(1);
    },
    maxTimeMetric() {
      if (!this.monitoring.length) return 10;
      const vals = this.monitoring.flatMap((m) => [
        m.latency || 0,
        m.request_time || 0,
      ]);
      const max = Math.max(...vals);
      if (max === 0) return 10;
      // arrotonda a step "puliti"
      const pow = Math.pow(10, Math.floor(Math.log10(max)));
      return Math.ceil((max * 1.05) / pow) * pow;
    },
    chartSeries() {
      return [
        {
          name: "Request Time (ms)",
          data: [
            ...this.monitoring.map((m) => ({
              x: m.ts * 1000,
              y: m.request_time || null,
            })),
          ],
        },
        {
          name: "RSSI (dBm)",
          data: [
            ...this.monitoring.map((m) => ({
              x: m.ts * 1000,
              y: Math.abs(m.RSSI) || null,
            })),
          ],
        },
        {
          name: "Error Rate (%)",
          data: [
            ...this.monitoring.map((m) => ({
              x: m.ts * 1000,
              y: m.error_rate >= 0 ? m.error_rate : null,
            })),
          ],
        },
        {
          name: "Latency (ms)",
          data: [
            ...this.monitoring.map((m) => ({
              x: m.ts * 1000,
              y: m.latency > 0 ? m.latency : null,
            })),
          ],
        },
      ];
    },
    chartOptions() {
      return {
        chart: {
          id: "therm-monitoring",
          toolbar: {
            show: false,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: false,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
        },
        colors: ["#008FFB", "#FEB019", "#FF4560", "#00E396"],
        stroke: { width: 3, curve: "smooth" },
        xaxis: {
          categories: this.monitoring.map((m) =>
            moment.unix(m.timestamp).format("HH:mm:ss")
          ),
          type: "datetime",
        },
        yaxis: [
          {
            seriesName: "Latency (ms)",
            title: { text: "Latency / Request Time (ms)" },
            min: 0,

            max: this.maxTimeMetric,
            labels: { formatter: (v) => Math.round(v) },
            show: false,
          },
          {
            seriesName: "RSSI (dBm)",
            opposite: true,
            title: { text: "RSSI (dBm)" },
            // Se RSSI negativo, gestisci range; se positivo rimuovi min/max
            min: 0,
            max: 100,
            labels: { formatter: (v) => Math.round(v) },
            show: false,
          },
          {
            seriesName: "Error Rate (%)",
            opposite: true,
            title: { text: "Error Rate (%)" },
            min: 0,
            max: 100,
            decimalsInFloat: 0,
            labels: { formatter: (v) => `${Math.round(v)}` },
            offsetX: 55,
            show: false,
          },
          {
            seriesName: "Request Time (ms)",
            title: { text: "Request Time (ms)" },
            min: 0,
            max: this.maxTimeMetric,
            labels: { formatter: (v) => Math.round(v) },
            offsetX: -55,
            show: false,
          },
        ],
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            format: "dd MMM HH:mm:ss",
          },
        },
        markers: { size: 0 },
      };
    },
    isRssiNegative() {
      if (!this.monitoring.length) return false;
      // se >50% dei valori è negativo assumiamo scala negativa
      const negatives = this.monitoring.filter((m) => (m.RSSI || 0) < 0).length;
      return negatives > this.monitoring.length / 2;
    },
  },
  methods: {
    open() {
      this.dialog = true;
    },
  },
};
</script>